import { OffshoreFuelSpecFormState } from '@navstation-portal/reporting/data-model/offshore-report';
import {
  FormSectionState,
  FormSectionStateArray,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption, StateField } from '@navstation-portal/shared/data-model/common';
import { signalStoreFeature } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { offshoreConsumptionsEvents } from './with-offshore-consumptions-events';

export function withOffshoreConsumptionsReducer() {
  return signalStoreFeature(
    withReducer(
      on(offshoreConsumptionsEvents.updateFuelSpec, (payload, state) => {
        const { index, lcv, sulphur, density, fuelTemp, viscosity, isoGrade } = payload.payload;
        const consumptionsState = state as Record<string, FormSectionState>;
        const fuelSpecs = consumptionsState['fuelSpecs'] as FormSectionState;
        const fsEntries = (fuelSpecs['entries'] as FormSectionStateArray) || [];
        const fsEntryToUpdate = fsEntries[index] as OffshoreFuelSpecFormState;
        if (
          !fsEntryToUpdate ||
          (lcv === undefined &&
            sulphur === undefined &&
            density === undefined &&
            fuelTemp === undefined &&
            viscosity === undefined &&
            isoGrade === undefined)
        ) {
          return state;
        }
        const updatedFsEntry: OffshoreFuelSpecFormState = {
          ...fsEntryToUpdate,
          isoFuelGrade:
            isoGrade === undefined
              ? fsEntryToUpdate?.isoFuelGrade
              : {
                  ...fsEntryToUpdate?.isoFuelGrade,
                  value: isoGrade,
                },
          density:
            density === undefined
              ? fsEntryToUpdate?.density
              : {
                  ...fsEntryToUpdate?.density,
                  value: density,
                },
          lcv:
            lcv === undefined
              ? fsEntryToUpdate?.lcv
              : {
                  ...fsEntryToUpdate?.lcv,
                  value: lcv,
                },
          sulphur:
            sulphur === undefined
              ? fsEntryToUpdate?.sulphur
              : {
                  ...fsEntryToUpdate?.sulphur,
                  value: sulphur,
                },
          fuelTemp:
            fuelTemp === undefined
              ? fsEntryToUpdate?.fuelTemp
              : {
                  ...fsEntryToUpdate?.fuelTemp,
                  value: fuelTemp,
                },
          viscosity:
            viscosity === undefined
              ? fsEntryToUpdate?.viscosity
              : {
                  ...fsEntryToUpdate?.viscosity,
                  value: viscosity,
                },
        };

        const updatedFsEntries = [...fsEntries];
        updatedFsEntries[index] = updatedFsEntry;

        return {
          ...state,
          fuelSpecs: {
            entries: updatedFsEntries,
          },
        };
      }),
      on(offshoreConsumptionsEvents.addFuelSpec, () => (state) => {
        const consumptionsState = state as Record<string, FormSectionState>;
        const hasEntries = (key: string) => Array.isArray(consumptionsState[key]?.['entries']);
        const getEntries = (key: string): FormSectionStateArray =>
          (consumptionsState[key]?.['entries'] as FormSectionStateArray) || [];

        const fsEntries = getEntries('fuelSpecs');
        const nextFsEntries = [
          ...fsEntries,
          {
            fuelType: initStateField<SelectOption>(),
            isoFuelGrade: initStateField<SelectOption>(),
            lcv: initStateField<number>(),
            sulphur: initStateField<number>(),
            density: initStateField<number>(),
            fuelTemp: initStateField<number>(),
            viscosity: initStateField<number>(),
          } as OffshoreFuelSpecFormState,
        ];

        return {
          ...state,
          fuelSpecs: {
            entries: nextFsEntries,
          },
          ...(hasEntries('totalConsumptions')
            ? {
                totalConsumptions: {
                  entries: [
                    ...getEntries('totalConsumptions'),
                    {
                      mainEngine: initStateField<number>(),
                      boiler: initStateField<number>(),
                      incinerator: initStateField<number>(),
                      otherEngines: initStateField<number>(),
                      total: initStateField<number>(),
                    },
                  ],
                },
              }
            : {}),
          ...(hasEntries('auxEngineConsumptions')
            ? {
                auxEngineConsumptions: {
                  entries: [...getEntries('auxEngineConsumptions'), createConsumptionEntry({ basicLoad: 10 })],
                },
              }
            : {}),
          ...(hasEntries('boilerConsumptions')
            ? {
                boilerConsumptions: {
                  entries: [...getEntries('boilerConsumptions'), createConsumptionEntry()],
                },
              }
            : {}),
        };
      }),
      on(offshoreConsumptionsEvents.removeFuelSpec, ({ payload: index }) => (state) => {
        if (index === 0) return state;
        const consumptionsState = state as Record<string, FormSectionState>;
        const hasEntries = (key: string) => Array.isArray(consumptionsState[key]?.['entries']);
        const getEntries = (key: string): FormSectionStateArray =>
          (consumptionsState[key]?.['entries'] as FormSectionStateArray) || [];
        const fsEntries = getEntries('fuelSpecs');

        const i = index ?? fsEntries.length - 1;

        return {
          ...state,
          fuelSpecs: {
            entries: [...fsEntries.slice(0, i), ...fsEntries.slice(i + 1)],
          },
          ...(hasEntries('totalConsumptions')
            ? {
                totalConsumptions: {
                  entries: [
                    ...getEntries('totalConsumptions').slice(0, i),
                    ...getEntries('totalConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
          ...(hasEntries('auxEngineConsumptions')
            ? {
                auxEngineConsumptions: {
                  entries: [
                    ...getEntries('auxEngineConsumptions').slice(0, i),
                    ...getEntries('auxEngineConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
          ...(hasEntries('boilerConsumptions')
            ? {
                boilerConsumptions: {
                  entries: [
                    ...getEntries('boilerConsumptions').slice(0, i),
                    ...getEntries('boilerConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
        };
      }),
      on(offshoreConsumptionsEvents.clearFuelSpec, ({ payload: i }) => (state) => {
        const consumptionsState = state as Record<string, FormSectionState>;
        const hasEntries = (key: string) => Array.isArray(consumptionsState[key]?.['entries']);
        const getEntries = (key: string): FormSectionStateArray =>
          (consumptionsState[key]?.['entries'] as FormSectionStateArray) || [];

        const fsEntries = getEntries('fuelSpecs');
        const fsEntryToClear = fsEntries[i] as OffshoreFuelSpecFormState;
        if (!fsEntryToClear) return state;

        return {
          ...state,
          fuelSpecs: {
            entries: [
              ...fsEntries.slice(0, i),
              {
                rowId: fsEntryToClear.rowId,
                fuelType: fsEntryToClear.fuelType,
                isoFuelGrade: initStateField<SelectOption>(),
                lcv: initStateField<number>(),
                sulphur: initStateField<number>(),
                density: initStateField<number>(),
                fuelTemp: initStateField<number>(),
                viscosity: initStateField<number>(),
              },
              ...fsEntries.slice(i + 1),
            ],
          },
          ...(hasEntries('totalConsumptions')
            ? {
                totalConsumptions: {
                  entries: [
                    ...getEntries('totalConsumptions').slice(0, i),
                    {
                      mainEngine: initStateField<number>(),
                      auxEngines: initStateField<number>(),
                      boiler: initStateField<number>(),
                      igGenerator: initStateField<number>(),
                      incinerator: initStateField<number>(),
                      dpp: initStateField<number>(),
                      otherEngines: initStateField<number>(),
                      cargoRelated: initStateField<number>(),
                      total: initStateField<number>(),
                    },
                    ...getEntries('totalConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
          ...(hasEntries('auxEngineConsumptions')
            ? {
                auxEngineConsumptions: {
                  entries: [
                    ...getEntries('auxEngineConsumptions').slice(0, i),
                    createConsumptionEntry({ basicLoad: 10 }),
                    ...getEntries('auxEngineConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
          ...(hasEntries('boilerConsumptions')
            ? {
                boilerConsumptions: {
                  entries: [
                    ...getEntries('boilerConsumptions').slice(0, i),
                    createConsumptionEntry(),
                    ...getEntries('boilerConsumptions').slice(i + 1),
                  ],
                },
              }
            : {}),
        };
      })
    )
  );
}

const createConsumptionEntry = (overrides?: { basicLoad?: number }): Record<string, StateField<StateFieldType>> => {
  return {
    basicLoad: initStateField<number>(overrides?.basicLoad ?? 0),
    ballastOperations: initStateField<number>(),
    cargoHeating: initStateField<number>(),
    tankCleaning: initStateField<number>(),
    cargoDischarging: initStateField<number>(),
    inertGasSystems: initStateField<number>(),
    bunkerHeating: initStateField<number>(),
  };
};
